import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType, useParams } from 'react-router-dom';
// The company font, served by the homepage itself.
import '@fontsource-variable/open-sans';
import './styles/theme.css';
import Navigation from './components/Navigation.jsx';
import LandingPage from './pages/LandingPage.jsx';
import TeamPage from './pages/TeamPage.jsx';
import TeamMemberPage from './pages/TeamMemberPage.jsx';
import EventsPage from './pages/EventsPage.jsx';
import FinancePage from './pages/FinancePage.jsx';
import TeachingPage from './pages/TeachingPage.jsx';
import IddPage from './pages/IddPage.jsx';
import VeridionProductPage from './pages/VeridionProductPage.jsx';
import KyveilPage from './pages/KyveilPage.jsx';
import QuantumPage from './pages/QuantumPage.jsx';
import PrivacyPage from './pages/PrivacyPage.jsx';
import AiTransparencyPage from './pages/AiTransparencyPage.jsx';
import ImpressumPage from './pages/ImpressumPage.jsx';
import AccountPage from './pages/AccountPage.jsx';
import AdminPage from './pages/AdminPage.jsx';
import VeridionPage from './pages/VeridionPage.jsx';
import VeridionPaioneerPage from './pages/VeridionPaioneerPage.jsx';
import CompanyMemoryPage from './pages/CompanyMemoryPage.jsx';
import SoftwareDevelopmentPage from './pages/SoftwareDevelopmentPage.jsx';
import AgenticRagPage from './pages/AgenticRagPage.jsx';
import AzuritePage from './pages/AzuritePage.jsx';
import SeoLandingPage from './pages/SeoLandingPage.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import { useCookieConsent } from './hooks/useCookieConsent.js';
import { markInternal, trackClick, trackPageview } from './utils/visitorTracking.js';
import { useTranslation } from './hooks/useTranslation.js';
import { supportedLanguageCodes } from './config/languages.js';
import { applyRouteSeo } from './utils/seo.js';
import { stripLanguageFromPath } from './utils/localizedRoutes.js';
import teamProfiles from './profile/index.cjs';

const SeoController = () => {
  const location = useLocation();
  const { currentLang } = useTranslation();

  useEffect(() => {
    applyRouteSeo(location.pathname, currentLang);
  }, [location.pathname, currentLang]);

  return null;
};

// The visitor beacon follows the consent (D-HP-CADDY-011): utils/visitorTracking.js sends nothing
// before a decision, events without identifier for "statistics" and the profile events for "all".
const VisitorAnalyticsController = ({ consentMode }) => {
  const location = useLocation();
  const { currentLang } = useTranslation();

  useEffect(() => {
    if (location.pathname.includes('/admin')) {
      markInternal();
      return;
    }
    trackPageview({ path: location.pathname, search: location.search, lang: currentLang, title: document.title });
  }, [location.pathname, location.search, currentLang, consentMode]);

  useEffect(() => {
    const handleClick = (event) => {
      if (window.location.pathname.includes('/admin')) return;
      const anchor = event.target?.closest?.('a[href], button.homepage-button-primary, button.homepage-button-secondary');
      if (!anchor || anchor.closest('[aria-labelledby="cookie-consent-title"]')) return;
      const href = anchor.getAttribute('href') || '';
      const isCta = anchor.classList.contains('homepage-button-primary')
        || anchor.classList.contains('homepage-button-secondary')
        || href.startsWith('mailto:')
        || href.startsWith('http')
        || href.includes('/shinyproxy')
        || href.includes('/oauth2');
      if (!isCta) return;
      trackClick((href || anchor.textContent || 'cta').trim().slice(0, 140));
    };
    document.addEventListener('click', handleClick, { capture: true, passive: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, []);

  return null;
};

// A link inside the homepage swaps the page in the same document, so the
// browser keeps the scroll position. A change of page therefore starts at the
// top. A change of language stays on the same page and keeps its place, and
// back and forward are left to the browser, which restores the earlier place.
const ScrollController = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const page = stripLanguageFromPath(location.pathname);

  useEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [page]);

  return null;
};

const LocalizedRoute = ({ children }) => {
  const { lang } = useParams();

  if (lang && !supportedLanguageCodes.includes(lang)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const withLocalizedRoute = (element) => <LocalizedRoute>{element}</LocalizedRoute>;

// The former about page: old links land on the founder's profile in the same language.
const LegacyAboutRedirect = () => {
  const { lang } = useParams();
  const target = `/team/${teamProfiles.legacyAboutSlug}`;

  return <Navigate to={lang ? `/${lang}${target}` : target} replace />;
};

const App = () => {
  const { cookieConsent, consentMode, showConsentModal, saveConsent, acceptAll, rejectAll, closeConsent } = useCookieConsent();

  // The one build-version/cache check lives in src/index.js (checkLegacyCache),
  // before React mounts. Do not add a second checker here: a duplicate forces an
  // extra reload on every first visit.
  return (
    <BrowserRouter>
      <div className="min-h-screen bg-bg-main text-text-primary">
        <SeoController />
        <VisitorAnalyticsController consentMode={consentMode} />
        <ScrollController />
        <Navigation />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/:lang" element={withLocalizedRoute(<LandingPage />)} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/:lang/team" element={withLocalizedRoute(<TeamPage />)} />
          <Route path="/team/:member" element={<TeamMemberPage />} />
          <Route path="/:lang/team/:member" element={withLocalizedRoute(<TeamMemberPage />)} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/:lang/events" element={withLocalizedRoute(<EventsPage />)} />
          <Route path="/about" element={<LegacyAboutRedirect />} />
          <Route path="/:lang/about" element={withLocalizedRoute(<LegacyAboutRedirect />)} />
          <Route path="/finance" element={<FinancePage />} />
          <Route path="/:lang/finance" element={withLocalizedRoute(<FinancePage />)} />
          <Route path="/teaching" element={<TeachingPage />} />
          <Route path="/:lang/teaching" element={withLocalizedRoute(<TeachingPage />)} />
          <Route path="/agenticdev" element={<IddPage />} />
          <Route path="/:lang/agenticdev" element={withLocalizedRoute(<IddPage />)} />
          <Route path="/veridion" element={<VeridionProductPage />} />
          <Route path="/:lang/veridion" element={withLocalizedRoute(<VeridionProductPage />)} />
          <Route path="/kyveil" element={<KyveilPage />} />
          <Route path="/:lang/kyveil" element={withLocalizedRoute(<KyveilPage />)} />
          <Route path="/quantum" element={<QuantumPage />} />
          <Route path="/:lang/quantum" element={withLocalizedRoute(<QuantumPage />)} />
          <Route path="/defence-ai-europe" element={<SeoLandingPage pageId="defenceAiEurope" />} />
          <Route path="/:lang/defence-ai-europe" element={withLocalizedRoute(<SeoLandingPage pageId="defenceAiEurope" />)} />
          <Route path="/ai-consulting-europe" element={<SeoLandingPage pageId="aiConsultingEurope" />} />
          <Route path="/:lang/ai-consulting-europe" element={withLocalizedRoute(<SeoLandingPage pageId="aiConsultingEurope" />)} />
          <Route path="/kyveil-pii-anonymization-europe" element={<SeoLandingPage pageId="kyveilEurope" />} />
          <Route path="/:lang/kyveil-pii-anonymization-europe" element={withLocalizedRoute(<SeoLandingPage pageId="kyveilEurope" />)} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/:lang/privacy" element={withLocalizedRoute(<PrivacyPage />)} />
          <Route path="/ai-transparency" element={<AiTransparencyPage />} />
          <Route path="/:lang/ai-transparency" element={withLocalizedRoute(<AiTransparencyPage />)} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/:lang/impressum" element={withLocalizedRoute(<ImpressumPage />)} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/:lang/account" element={withLocalizedRoute(<AccountPage />)} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/:lang/admin" element={withLocalizedRoute(<AdminPage />)} />
          <Route path="/admin/veridion" element={<VeridionPage />} />
          <Route path="/:lang/admin/veridion" element={withLocalizedRoute(<VeridionPage />)} />
          <Route path="/admin/veridion-paioneer" element={<VeridionPaioneerPage />} />
          <Route path="/:lang/admin/veridion-paioneer" element={withLocalizedRoute(<VeridionPaioneerPage />)} />
          <Route path="/admin/memory" element={<CompanyMemoryPage />} />
          <Route path="/:lang/admin/memory" element={withLocalizedRoute(<CompanyMemoryPage />)} />
          <Route path="/admin/software" element={<SoftwareDevelopmentPage />} />
          <Route path="/:lang/admin/software" element={withLocalizedRoute(<SoftwareDevelopmentPage />)} />
          <Route path="/admin/agentic-rag" element={<AgenticRagPage />} />
          <Route path="/:lang/admin/agentic-rag" element={withLocalizedRoute(<AgenticRagPage />)} />
          <Route path="/admin/azurite" element={<AzuritePage />} />
          <Route path="/:lang/admin/azurite" element={withLocalizedRoute(<AzuritePage />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {showConsentModal && (
          <CookieConsent
            consent={cookieConsent}
            onSave={saveConsent}
            onAcceptAll={acceptAll}
            onRejectAll={rejectAll}
            onClose={closeConsent}
          />
        )}
      </div>
    </BrowserRouter>
  );
};

export default App;
